import axios, { AxiosInstance } from 'axios';

import { Configuration, StorageFileDto } from 'src/Services/API/generated';

// Hand-written like EntityLinks.api.ts: the preview API cannot regenerate the client yet.

export enum MemoryKind {
  Family = 'FAMILY',
  Celebration = 'CELEBRATION',
  House = 'HOUSE',
  Works = 'WORKS',
  InMemory = 'IN_MEMORY',
}

export enum MemoryDatePrecision {
  Day = 'DAY',
  Month = 'MONTH',
  Season = 'SEASON',
  Year = 'YEAR',
  Decade = 'DECADE',
}

export enum MemoryTranscriptStatus {
  Pending = 'PENDING',
  Done = 'DONE',
  Failed = 'FAILED',
}

export interface MemoryStorytellerDto {
  displayName: string;
  initials: string;
}

export interface MemoryRoomDto {
  id: string;
  name: string;
}

export interface MemoryPersonDto {
  id: string;
  memberId: string | null;
  displayName: string;
}

export interface MemoryTimelineItemDto {
  id: string;
  title: string;
  memoryKind: MemoryKind | null;
  takenAt: string | null;
  takenAtPrecision: MemoryDatePrecision;
  takenAtApproximate: boolean;
  cover: StorageFileDto | null;
  photoCount: number;
  recordingCount: number;
  voiceSeconds: number;
  rooms: MemoryRoomDto[];
  storytellers: MemoryStorytellerDto[];
  people: MemoryPersonDto[];
  createdAt: string;
}

export interface MemoryStatsDto {
  firstYear: number | null;
  memoryCount: number;
  photoCount: number;
  voiceSeconds: number;
  storytellerCount: number;
}

export interface MemoryTimelineDto {
  stats: MemoryStatsDto;
  items: MemoryTimelineItemDto[];
}

export interface MemoryRecordingDto {
  id: string;
  audioUrl: string | null;
  audioMimeType: string | null;
  durationSeconds: number;
  speakerDisplayName: string;
  speakerId: string | null;
  recordedAt: string;
  transcript: string | null;
  transcriptStatus: MemoryTranscriptStatus;
  withdrawn: boolean;
  canDelete: boolean;
}

export interface MemoryDto extends MemoryTimelineItemDto {
  description: string | null;
  place: string | null;
  files: StorageFileDto[];
  recordings: MemoryRecordingDto[];
  author: { id: string; displayName: string };
  canEdit: boolean;
}

export interface MemoryPersonInput {
  memberId?: string;
  freeName?: string;
}

export interface CreateMemoryInput {
  title: string;
  description?: string;
  memoryKind?: MemoryKind | null;
  takenAt?: string | null;
  takenAtPrecision?: MemoryDatePrecision;
  takenAtApproximate?: boolean;
  fileIds?: string[];
  roomIds?: string[];
  people?: MemoryPersonInput[];
  place?: string | null;
}

export type UpdateMemoryInput = Partial<CreateMemoryInput>;

export interface MemoryDraftDto {
  transcript: string;
  title: string;
  memoryKind: MemoryKind | null;
  takenAt: string | null;
  takenAtPrecision: MemoryDatePrecision;
  takenAtApproximate: boolean;
  roomIds: string[];
  memberIds: string[];
  freeNames: string[];
  description: string;
}

export interface AddMemoryRecordingInput {
  audio: Blob;
  filename: string;
  durationSeconds: number;
  speakerId?: string;
  speakerName?: string;
  /** Known when Lucien drafted the memory from this story: skips a second transcription. */
  transcript?: string;
  language?: 'fr' | 'en';
}

export class MemoriesApi {
  private axiosInstance: AxiosInstance;

  constructor(configuration: Configuration) {
    this.axiosInstance = axios.create({
      baseURL: configuration.basePath ?? '',
      ...configuration.baseOptions,
    });
  }

  async timeline(
    assetId: string,
    filters: { kind?: MemoryKind; withVoice?: boolean } = {}
  ): Promise<MemoryTimelineDto> {
    const response = await this.axiosInstance.get<MemoryTimelineDto>(
      `/assets/${assetId}/memories`,
      { params: filters }
    );
    return response.data;
  }

  async findOne(assetId: string, memoryId: string): Promise<MemoryDto> {
    const response = await this.axiosInstance.get<MemoryDto>(
      `/assets/${assetId}/memories/${memoryId}`
    );
    return response.data;
  }

  async create(assetId: string, data: CreateMemoryInput): Promise<MemoryDto> {
    const response = await this.axiosInstance.post<MemoryDto>(`/assets/${assetId}/memories`, data);
    return response.data;
  }

  async update(assetId: string, memoryId: string, data: UpdateMemoryInput): Promise<MemoryDto> {
    const response = await this.axiosInstance.patch<MemoryDto>(
      `/assets/${assetId}/memories/${memoryId}`,
      data
    );
    return response.data;
  }

  async remove(assetId: string, memoryId: string): Promise<void> {
    await this.axiosInstance.delete(`/assets/${assetId}/memories/${memoryId}`);
  }

  async addRecording(
    assetId: string,
    memoryId: string,
    input: AddMemoryRecordingInput
  ): Promise<MemoryRecordingDto> {
    const form = new FormData();
    form.append('file', input.audio, input.filename);
    form.append('durationSeconds', String(Math.max(1, input.durationSeconds)));
    if (input.speakerId) form.append('speakerId', input.speakerId);
    if (input.speakerName) form.append('speakerName', input.speakerName);
    if (input.transcript) form.append('transcript', input.transcript);
    if (input.language) form.append('language', input.language);
    const response = await this.axiosInstance.post<MemoryRecordingDto>(
      `/assets/${assetId}/memories/${memoryId}/recordings`,
      form
    );
    return response.data;
  }

  /** Lucien listens to a story and proposes the memory card. Nothing is saved. */
  async draftFromVoice(
    assetId: string,
    input: { audio: Blob; filename: string; language: 'fr' | 'en' }
  ): Promise<MemoryDraftDto> {
    const form = new FormData();
    form.append('file', input.audio, input.filename);
    form.append('language', input.language);
    const response = await this.axiosInstance.post<MemoryDraftDto>(
      `/assets/${assetId}/memories/draft-from-voice`,
      form
    );
    return response.data;
  }

  async removeRecording(assetId: string, memoryId: string, recordingId: string): Promise<void> {
    await this.axiosInstance.delete(
      `/assets/${assetId}/memories/${memoryId}/recordings/${recordingId}`
    );
  }
}
